import React, { useState } from 'react'
import { cn } from 'lib/utils'
import { transformUrl, TRANSFORM } from 'components/FileUploader/fileUploadUtils'
import NotesRailSection from './NotesRailSection'
import type { NoteImageEntry } from './notesWorkspace.types'

interface NotesRailGalleryProps {
	images: NoteImageEntry[]
	/** Scrolls to the owning note and opens it at this image. */
	onSelect: (entry: NoteImageEntry) => void
	className?: string
}

/** Thumbnails shown before the section offers to expand. */
const COLLAPSED_COUNT = 9

/**
 * Every image across the visible notes.
 *
 * This is the section that earns the rail on an image-heavy surface: notes bury
 * their pictures one card at a time, and there is otherwise no way to see what
 * a site or portfolio actually looks like without scrolling the whole feed.
 *
 * It reflects the *filtered* set, so it stays connected to the feed rather than
 * being a second, independent gallery.
 */
const NotesRailGallery: React.FC<NotesRailGalleryProps> = ({ images, onSelect, className }) => {
	const [expanded, setExpanded] = useState(false)

	if (images.length === 0) return null

	const visible = expanded ? images : images.slice(0, COLLAPSED_COUNT)
	const hidden = images.length - visible.length

	return (
		<NotesRailSection
			title="Images"
			action={<span className="text-xs tabular-nums text-muted-foreground">{images.length}</span>}
			className={className}
		>
			<div className="grid grid-cols-3 gap-1.5">
				{visible.map((entry, index) => (
					<button
						key={`${entry.noteId}-${entry.url}-${index}`}
						type="button"
						onClick={() => onSelect(entry)}
						aria-label={`Open image ${index + 1} in its note`}
						data-testid="notes-rail-thumb"
						className={cn(
							'aspect-square overflow-hidden rounded-md border border-border bg-muted p-0',
							'transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
						)}
					>
						<img
							src={transformUrl(entry.url, TRANSFORM.THUMB)}
							alt=""
							loading="lazy"
							className="h-full w-full object-cover"
						/>
					</button>
				))}
			</div>

			{(hidden > 0 || expanded) && (
				<button
					type="button"
					onClick={() => setExpanded(current => !current)}
					className="mt-2 w-full rounded-md py-1 text-xs font-medium text-primary transition-colors hover:bg-primary/10"
				>
					{expanded ? 'Show fewer' : `Show ${hidden} more`}
				</button>
			)}
		</NotesRailSection>
	)
}

export default NotesRailGallery
